import { useEffect, useId, useState } from "react";
import { Alert, Box } from "@mui/material";
import PpLogo from "../common/PpLogo";
import { BuilderCard, CardHeading, PrimaryButton } from "./builderUi";
import { candidateAmount, candidateCard, candidateDate } from "../../utils/bankCaseUtils";
import { providerLabel } from "../../utils/textUtils";
import { OrderCandidate, OrderSearchResult } from "../../types/evidenceBankCase";
import { EvidenceOrderSourceKind } from "../../types/evidence";

const keyOf = (candidate: OrderCandidate) => `${candidate.source}:${candidate.accountId}:${candidate.orderKey}`;

function details(candidate: OrderCandidate): string {
  return [
    candidate.customerEmail,
    candidateCard(candidate),
    candidate.placedOn && candidateDate(candidate.placedOn),
    candidate.matchedOn === "amount_and_date" && "matched on amount and date",
  ].filter(Boolean).join(" · ");
}

function outage(unavailable: EvidenceOrderSourceKind[], anyFound: boolean): string | null {
  if (unavailable.length === 0) return null;
  const names = unavailable.map(providerLabel);
  if (unavailable.length === 2) return `${names.join(" and ")} did not answer. Try again in a minute.`;
  const other = providerLabel(unavailable[0] === "shopify" ? "checkout_champ" : "shopify");
  return anyFound
    ? `${names[0]} did not answer. These are the ${other} orders only.`
    : `${names[0]} did not answer, and ${other} has no such order. Try again in a minute.`;
}

export default function OrderCandidates({ result, searchedBy, onContinue }: {
  result: OrderSearchResult;
  searchedBy: "payment" | "order";
  onContinue: (candidate: OrderCandidate) => void;
}) {
  const { candidates, truncated, unavailable, incomplete } = result;
  const [picked, setPicked] = useState<string | null>(null);
  const group = useId();
  // A new result is a new choice; a single order needs none.
  useEffect(() => { setPicked(candidates.length === 1 ? keyOf(candidates[0]) : null); }, [candidates]);

  const warning = outage(unavailable, candidates.length > 0);
  const chosen = candidates.find((candidate) => keyOf(candidate) === picked);
  const count = candidates.length === 1 ? "1 order" : `${candidates.length} orders`;

  return (
    <BuilderCard sx={{ gap: "12px" }}>
      {warning && <Alert severity="warning">{warning}</Alert>}
      {incomplete.length > 0 && (
        <Alert severity="warning">{incomplete.join(", ")} did not answer. The same order number may be there too. Try again in a minute.</Alert>
      )}

      {candidates.length === 0 && unavailable.length === 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Box sx={{ fontWeight: 600 }}>{searchedBy === "payment" ? "No order matches." : "No order has this number."}</Box>
          {searchedBy === "payment" && (
            <Box sx={{ color: "#475467" }}>Check the amount and date against the processor, or try the order ID.</Box>
          )}
        </Box>
      )}

      {candidates.length > 0 && (
        <>
          <CardHeading>{searchedBy === "payment" ? `${count} match` : `${count} found`}</CardHeading>
          <Box role="radiogroup" aria-label="Orders" sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {candidates.map((candidate) => {
              const key = keyOf(candidate);
              const isPicked = key === picked;
              return (
                <Box
                  key={key}
                  component="label"
                  sx={{
                    display: "grid", gridTemplateColumns: "20px 22px 1fr auto", gap: "12px", alignItems: "center", borderRadius: "8px", cursor: "pointer",
                    ...(isPicked
                      ? { border: "2px solid #465fff", p: "12px 14px", bgcolor: "#f5f7ff" }
                      : { border: "1px solid #e4e7ec", p: "13px 15px" }),
                  }}
                >
                  <input
                    type="radio"
                    name={group}
                    value={key}
                    checked={isPicked}
                    onChange={() => setPicked(key)}
                    style={{ width: 16, height: 16, margin: 0, accentColor: "#465fff" }}
                  />
                  <PpLogo ppType={candidate.source} size={18} />
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "3px", minWidth: 0 }}>
                    <Box sx={{ fontWeight: 600 }}>{candidate.orderName}</Box>
                    <Box sx={{ color: "#475467" }}>{details(candidate)}</Box>
                  </Box>
                  <Box sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                    {candidateAmount(candidate.amount, candidate.currency)}
                  </Box>
                </Box>
              );
            })}
          </Box>
          {truncated && (
            <Box sx={{ fontSize: 12, color: "#667085" }}>
              More orders match than the 20 shown. Add the card or use the order ID.
            </Box>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", pt: "4px" }}>
            <PrimaryButton disabled={!chosen} onClick={() => chosen && onContinue(chosen)}>Continue</PrimaryButton>
          </Box>
        </>
      )}
    </BuilderCard>
  );
}
